import React, { useEffect, useState } from 'react';
import { traitsAPI, leaguesAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Tag, Star, Check, Flame, Footprints, Swords } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getTraitStyle, CATEGORY_STYLE, PLAY_STYLES } from '@/lib/traitMeta';
import { SkillStars, FormMeter, formBreakdownLabel } from './PlayerTraits';

// ponytail: what calcetto has, used until GET /traits answers (or if it never does)
const CALCETTO_ATTRIBUTES = [
  { key: 'skill_level', type: 'stars05' },
  { key: 'form', type: 'stars05', computed: true },
  { key: 'preferred_foot', type: 'enum', options: [
    { key: 'right', label: 'Destro' }, { key: 'left', label: 'Sinistro' }, { key: 'both', label: 'Ambidestro' },
  ] },
  { key: 'strong_foot', type: 'stars05' },
  { key: 'weak_foot', type: 'stars05' },
  { key: 'play_style', type: 'enum', options: PLAY_STYLES },
  { key: 'traits', type: 'traits' },
];

const HalfSlider = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-3" data-testid={testId}>
    <Slider
      value={[value]}
      onValueChange={([v]) => onChange(v)}
      min={1}
      max={5}
      step={0.5}
      className="flex-1"
    />
    <span className="w-8 text-right text-sm font-semibold tabular-nums">{Number(value).toFixed(1)}</span>
  </div>
);

/**
 * Dialog for editing a league member's attributes for the league's sport:
 * skill (half stars), the admin's manual form override, foot, weak foot and
 * up to N trait tags. Which sections appear comes from the sport's catalog.
 *
 * Used by both the public "Membri" tab (admin-only entry point) and
 * the admin "Gestione Membri" panel.
 */
export const MemberProfileDialog = ({ open, onOpenChange, leagueId, sportId, member, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [attributes, setAttributes] = useState(CALCETTO_ATTRIBUTES);
  const [maxTraits, setMaxTraits] = useState(3);
  const [traits, setTraits] = useState([]);
  const [skill, setSkill] = useState(3);
  const [strongFoot, setStrongFoot] = useState(3);
  const [weakFoot, setWeakFoot] = useState(3);
  const [foot, setFoot] = useState(null);
  const [playStyle, setPlayStyle] = useState(null);
  const [manualOn, setManualOn] = useState(false);
  const [formManual, setFormManual] = useState(3);
  const [saving, setSaving] = useState(false);

  const attrs = member?.attributes || member || {};
  const computedForm = Number(attrs.form_computed ?? attrs.form ?? 3);
  const breakdown = attrs.form_breakdown;
  const has = (key) => attributes.some((a) => a.key === key);
  const footOptions = attributes.find((a) => a.key === 'preferred_foot')?.options || [];
  const styleOptions = attributes.find((a) => a.key === 'play_style')?.options || [];

  useEffect(() => {
    if (!open) return;
    traitsAPI.getAll(sportId)
      .then((res) => {
        setCatalog(res.data.traits || []);
        if (res.data.attributes?.length) setAttributes(res.data.attributes);
        if (res.data.max_per_player) setMaxTraits(res.data.max_per_player);
      })
      .catch(() => {/* catalog non critico */});
  }, [open, sportId]);

  useEffect(() => {
    if (open && member) {
      const a = member.attributes || member;
      setTraits([...(a.traits || [])]);
      setSkill(Number(a.skill_level ?? 3));
      setStrongFoot(Number(a.strong_foot ?? 3));
      setWeakFoot(Number(a.weak_foot ?? 3));
      setFoot(a.preferred_foot || null);
      setPlayStyle(a.play_style || null);
      setManualOn(a.form_manual != null);
      setFormManual(Number(a.form_manual ?? a.form ?? 3));
    }
  }, [open, member]);

  const toggleTrait = (key) => {
    setTraits((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= maxTraits) {
        toast.error(`Massimo ${maxTraits} tag per giocatore`);
        return prev;
      }
      return [...prev, key];
    });
  };

  const handleSave = async () => {
    if (!member) return;
    setSaving(true);
    try {
      const body = { skill_level: skill, form_manual: manualOn ? formManual : null };
      if (has('traits')) body.traits = traits;
      if (has('preferred_foot') && foot) body.preferred_foot = foot;
      if (has('strong_foot')) body.strong_foot = strongFoot;
      if (has('weak_foot')) body.weak_foot = weakFoot;
      if (has('play_style') && playStyle) body.play_style = playStyle;
      const res = await leaguesAPI.updateMemberProfile(leagueId, member.user_id, body);
      toast.success('Profilo aggiornato');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5" />
            Profilo di {member?.user?.name || member?.name || 'giocatore'}
          </DialogTitle>
          <DialogDescription>
            Livello e caratteristiche guidano il generatore di squadre. La forma si calcola dai risultati.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Skill 1-5, half steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" /> Livello giocatore
              </p>
              <SkillStars level={skill} size="md" />
            </div>
            {attrs.skill_updated_by_name && (
              <p className="text-xs text-muted-foreground mb-2" data-testid="skill-updated-by">
                Ultima modifica: {attrs.skill_updated_by_name}
                {attrs.skill_updated_at && ` · ${new Date(attrs.skill_updated_at).toLocaleDateString('it-IT')}`}
                {attrs.skill_previous != null && ` · prima era ${Number(attrs.skill_previous).toFixed(1)}`}
              </p>
            )}
            <HalfSlider value={skill} onChange={setSkill} testId="skill-slider" />
            <p className="text-xs text-muted-foreground mt-1">
              1 = scarso, 3 = nella media, 5 = top player. Mezze stelle ammesse.
            </p>
          </div>

          {/* Form: computed, with an admin override */}
          {has('form') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-500" /> Forma
                </p>
                <FormMeter level={manualOn ? formManual : computedForm} breakdown={breakdown} size="md" />
              </div>
              <p className="text-xs text-muted-foreground">
                Calcolata dai risultati: {computedForm.toFixed(1)} · {formBreakdownLabel(breakdown)}.
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm">Imposta manualmente</span>
                <Switch checked={manualOn} onCheckedChange={setManualOn} data-testid="form-manual-switch" />
              </div>
              {manualOn && (
                <div className="mt-2 space-y-1">
                  <HalfSlider value={formManual} onChange={setFormManual} testId="form-slider" />
                  <p className="text-xs text-orange-600">
                    Override attivo: vince sul calcolo finché non lo disattivi.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Preferred foot */}
          {has('preferred_foot') && (
            <div>
              <p className="text-sm font-medium flex items-center gap-2 mb-2">
                <Footprints className="h-4 w-4 text-muted-foreground" /> Piede preferito
              </p>
              <div className="flex gap-2">
                {footOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setFoot(opt.key)}
                    className={cn(
                      'flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition-colors',
                      foot === opt.key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-muted hover:bg-muted',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {has('strong_foot') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Piede forte</p>
                <SkillStars level={strongFoot} size="md" />
              </div>
              <HalfSlider value={strongFoot} onChange={setStrongFoot} testId="strong-foot-slider" />
              <p className="text-xs text-muted-foreground mt-1">
                Quanto calcia bene con il piede preferito.
              </p>
            </div>
          )}

          {/* Weak foot */}
          {has('weak_foot') && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Piede debole</p>
                <SkillStars level={weakFoot} size="md" />
              </div>
              <HalfSlider value={weakFoot} onChange={setWeakFoot} testId="weak-foot-slider" />
              <p className="text-xs text-muted-foreground mt-1">
                Quanto è affidabile con il piede non preferito. Pesa poco, ma pesa.
              </p>
            </div>
          )}

          {/* Attacking / defending attitude */}
          {has('play_style') && (
            <div>
              <p className="text-sm font-medium flex items-center gap-2 mb-2">
                <Swords className="h-4 w-4 text-muted-foreground" /> Attitudine
              </p>
              <div className="flex gap-2">
                {styleOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setPlayStyle(opt.key)}
                    className={cn(
                      'flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition-colors',
                      playStyle === opt.key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-muted hover:bg-muted',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Il generatore di squadre divide attaccanti e difensori in modo equo.
              </p>
            </div>
          )}

          {/* Traits grouped by category */}
          {has('traits') && (
            <div>
              <p className="text-sm font-medium mb-2">
                Tag caratteristici <span className="text-muted-foreground">({traits.length}/{maxTraits})</span>
              </p>
              <ScrollArea className="h-64 border rounded-lg">
                <div className="p-3 space-y-4">
                  {['offensive', 'defensive', 'transversal'].map((cat) => {
                    const items = catalog.filter((t) => t.category === cat);
                    if (items.length === 0) return null;
                    const cs = CATEGORY_STYLE[cat];
                    return (
                      <div key={cat}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={cn('h-2 w-2 rounded-full', cs.dot)} />
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {cat === 'offensive' ? 'Offensivi' : cat === 'defensive' ? 'Difensivi' : 'Trasversali'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 gap-1.5">
                          {items.map((t) => {
                            const isSelected = traits.includes(t.key);
                            const isMax = !isSelected && traits.length >= maxTraits;
                            const { Icon, style } = getTraitStyle(t);
                            return (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() => toggleTrait(t.key)}
                                disabled={isMax}
                                className={cn(
                                  'w-full text-left p-2.5 rounded-lg border-2 transition-all',
                                  isSelected
                                    ? `${style.badge} border-current shadow-sm`
                                    : isMax
                                      ? 'opacity-40 cursor-not-allowed border-transparent'
                                      : 'border-transparent hover:bg-muted',
                                )}
                              >
                                <div className="flex items-center gap-2.5">
                                  <span className={cn(
                                    'h-8 w-8 rounded-lg flex items-center justify-center shrink-0',
                                    isSelected ? 'bg-white/70 dark:bg-black/30' : 'bg-muted',
                                  )}>
                                    {Icon ? <Icon className={cn('h-4 w-4', style.icon)} strokeWidth={2.2} /> : null}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-semibold text-sm">{t.label}</span>
                                      {isSelected && <Check className="h-3.5 w-3.5 opacity-70" />}
                                    </div>
                                    <p className={cn(
                                      'text-xs mt-0.5',
                                      isSelected ? 'opacity-80' : 'text-muted-foreground',
                                    )}>
                                      {t.description}
                                    </p>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva profilo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MemberProfileDialog;
